import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/hooks/useNotifications";
import { useMarkAsRead } from "@/hooks/domains/notifications/useNotifications";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Notification, NotificationType } from "@/types/notification";
import { getProfileUrl } from "@/utils/profileRedirects";
import NotificationFilters from "./NotificationFilters";
import NotificationsList from "./NotificationsList";
import { useAuth } from "@/hooks";

interface TestNotificationResult {
  notificationResult: string | null;
  notificationError: string | null;
  functionError: string | null;
  emailSent: boolean;
}

const NotificationsContent: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const markAsReadMutation = useMarkAsRead();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const { data: notificationsResponse, isLoading } = useNotifications(
    user?.id || undefined,
    pageSize,
    (currentPage - 1) * pageSize
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  // Extract notifications data and handle pagination
  const notifications = notificationsResponse?.data || [];
  const totalCount = notificationsResponse?.count || 0;

  const filteredNotifications = notifications.filter((notification) => {
    // Filter by tab: "all" shows everything, "unopened" shows only unread notifications
    const matchesTab = activeTab === "all" || (activeTab === "unopened" && !notification.read);
    const matchesSearch =
      !searchTerm ||
      notification.actor_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / pageSize);
  const pagination = {
    currentPage,
    totalPages,
    hasNextPage: currentPage < totalPages,
    hasPrevPage: currentPage > 1,
    totalCount,
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle notification actions
  const handleNotificationAction = useCallback(
    (notification: Notification) => {
      // Mark notification as read
      markAsReadMutation.mutate(notification.id);

      // Navigate based on notification type
      switch (notification.type) {
        case "portfolio_request":
          navigate("/dashboard/portfolio");
          break;
        case "experience_request":
          navigate("/dashboard/experience");
          break;
        case "like":
        case "view":
        case "profile_view":
          // Navigate to the actor's profile if they are authenticated
          if (notification.actor_id && !notification.is_anonymous) {
            const profileUrl = getProfileUrl(notification.actor_id);
            window.open(profileUrl, "_blank");
          } else {
            // Fallback to notifications page for anonymous views
            navigate("/notifications");
          }
          break;
        default:
          navigate("/notifications");
      }
    },
    [markAsReadMutation, navigate]
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <h1 className="text-xl sm:text-base font-medium">Notifications</h1>
      </div>

      <div className="space-y-6">
        <NotificationFilters
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />

        <div className="bg-white">
          <NotificationsList
            notifications={filteredNotifications as Notification[]}
            isLoading={isLoading}
            pagination={pagination}
            onPageChange={handlePageChange}
            onNotificationAction={handleNotificationAction}
          />
        </div>
      </div>
    </div>
  );
};

export default NotificationsContent;
